import { createTransport } from '@wheely/web-auth-lib/legacy';
import { callCbWithPageSpanContext } from '@wheely/web-tracing';
import { AxiosInstance, RawAxiosRequestHeaders, isAxiosError } from 'axios';
import { v4 as uuid } from 'uuid';

import { sessionManager } from '@/modules/auth/api';
import { v3Transport } from '@/modules/auth/api/v3';
import { AUTH_MODES, getAuthMode } from '@/modules/auth/mode';
import { errorHandling } from '@/services/errorHandling';
import { userLanguage } from '@/services/userLanguage';
import type { TMethod } from '@src-common/api/src/method';
import { SerializableError } from '@src-common/utils/SerializableError';

import { FetchingStatusStoreInstance } from '../store/fetching-status-store';
import { deleteCookie } from '../utils/cookie';
import { AUTH_COOKIE_KEY } from '../utils/session';

class LoginRequiredError extends SerializableError('LoginRequiredError') {}

class NotFoundError extends SerializableError('NotFoundError') {}

const legacyTransport = callCbWithPageSpanContext(
  createTransport({
    sessionManager,
  }),
) as AxiosInstance;

const v3TransportWithTraces = callCbWithPageSpanContext(v3Transport) as AxiosInstance;

// Picked per call: a legacy sign-out removes the V6 session record, and the
// very next request has to go out through the V3 stack without a reload.
const pickTransport = () =>
  getAuthMode() === AUTH_MODES.LEGACY ? legacyTransport : v3TransportWithTraces;

const PATH_WITHOUT_AUTH = '/auth/oauth';

const create =
  (
    httpMethod: string,
    getHeaders: () => Promise<RawAxiosRequestHeaders> = () => Promise.resolve({}),
    preparePayload = (x: any): any => x,
  ): TMethod =>
  async (path: string, payload: any, headers?: RawAxiosRequestHeaders) => {
    const isPathWithoutAuth = path.includes(PATH_WITHOUT_AUTH);
    const requestId = uuid();

    try {
      const defaultHeaders = await getHeaders();

      FetchingStatusStoreInstance.addRequest(requestId);

      const result = await pickTransport()({
        url: path,
        headers: {
          ...defaultHeaders,
          ...headers,
        },
        data: payload && preparePayload(payload),
        method: httpMethod,
      });

      FetchingStatusStoreInstance.removeRequest(requestId);

      return result.data;
    } catch (error) {
      FetchingStatusStoreInstance.removeRequest(requestId);

      if (isAxiosError(error)) {
        // eslint-disable-next-line no-console
        console.debug('Axios Error');
        // eslint-disable-next-line no-console
        console.debug('Axios Error information', error.toJSON());
        // eslint-disable-next-line no-console
        console.debug('Error message', error.message);

        if (error.code) {
          // eslint-disable-next-line no-console
          console.debug('Error code', error.code);
        }

        if (error.status) {
          // eslint-disable-next-line no-console
          console.debug('Error status', error.status);
        }

        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx

          // eslint-disable-next-line no-console
          console.debug('Error response status:', error.response.status);
          // eslint-disable-next-line no-console
          console.debug('Error response status text:', error.response.statusText);
          // eslint-disable-next-line no-console
          console.debug('Error response data:', JSON.stringify(error.response.data));

          if (error.response.status === 404) {
            throw new NotFoundError(
              error.response.data?.errors?.entity_was_removed_or_not_found ?? 'Not found',
            );
          }

          if (error.response.status === 429) {
            throw new Error(
              error.response.data?.errors?.throttling?.[0] ??
                'Too many requests. Please try again later',
              { cause: error },
            );
          }

          if (error.response.status === 500) {
            throw new Error('Something went wrong. Please try again later', { cause: error });
          }

          // Only the legacy stack signs out through the HTTP error chain; in
          // the V3 stack the client emits signedOut and the app resets there.
          if (
            error.response.status === 401 &&
            !isPathWithoutAuth &&
            getAuthMode() === AUTH_MODES.LEGACY
          ) {
            deleteCookie(AUTH_COOKIE_KEY);

            throw new LoginRequiredError();
          }

          errorHandling.captureException(error);

          return error.response.data || {};
        } else {
          // The request was made but no response was received or
          // something happened in setting up the request that triggered an Error

          throw new Error(error.message, { cause: error });
        }
      }

      // eslint-disable-next-line no-console
      console.debug('Non-Axios Error');

      errorHandling.captureException(error);

      if (error instanceof Error) {
        throw error;
      }
    }
  };

/**
 * An authenticated binary GET: the picked transport signs it with the session
 * of whichever auth mode owns the visit, the caller gets the raw Blob. Unlike
 * create(), every failure throws — a download has no partial-success shape.
 */
const getBlob = async (path: string): Promise<Blob> => {
  const requestId = uuid();

  FetchingStatusStoreInstance.addRequest(requestId);

  try {
    const result = await pickTransport()({
      url: path,
      method: 'GET',
      responseType: 'blob',
      headers: { 'accept-language': userLanguage.getLanguage() },
    });

    return result.data;
  } finally {
    FetchingStatusStoreInstance.removeRequest(requestId);
  }
};

const getContentTypeJSONHeaders = (): Promise<RawAxiosRequestHeaders> =>
  Promise.resolve({
    'content-type': 'application/json; charset=UTF-8',
    'accept-language': userLanguage.getLanguage(),
  });

const get = create('GET', getContentTypeJSONHeaders, (x: any) => JSON.stringify(x));
const post = create('POST', getContentTypeJSONHeaders, (x: any) => JSON.stringify(x));
const put = create('PUT', getContentTypeJSONHeaders, (x: any) => JSON.stringify(x));
const del = create('DELETE', getContentTypeJSONHeaders, (x: any) => JSON.stringify(x));
const patch = create('PATCH', getContentTypeJSONHeaders, (x: any) => JSON.stringify(x));
const postForm = create('POST');

export { get, getBlob, post, del, put, postForm, patch, LoginRequiredError, NotFoundError };
